"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { config } from "@/lib/config";
import { resolveLink } from "@/utils";
import { motion } from "framer-motion";
import { DrupalNode } from "next-drupal";
import Link from "next/link";
import { BsDownload } from "react-icons/bs";
import { FaChevronRight } from "react-icons/fa6";
import { MdChevronRight } from "react-icons/md";

interface FAQProps {
  data: DrupalNode;
}

export default function FAQSection({ data }: FAQProps) {
  const isWhite = data?.field_background_color === "white";
  const isBlue = data?.field_background_color === "blue";
  const isDarkGray = data?.field_background_color === "dark_gray";
  const isGray = !isWhite && !isBlue && !isDarkGray;
  const isLightBg = isGray || isWhite;

  const bgClass = isWhite
    ? "bg-white"
    : isBlue
      ? "bg-[#003350]"
      : isDarkGray
        ? "bg-mapGray"
        : "bg-[#ecedf6]";

  return (
    <div
      className={`${bgClass} relative py-[20px] betweenMobileTab:py-16 mobileMax:py-10 overflow-hidden`}
    >
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{
          type: "spring",
          duration: 2.5,
        }}
        className="absolute pointer-events-none bottom-[-50px] left-[-40px] z-[0] max-w-[30%] betweenMobileTab:max-w-[60%] mobileMax:max-w-[80%]"
      >
        <img
          src="/static/images/demand-flexiblity-bg-3.svg"
          alt="overlay-bg"
          className="opacity-70 betweenMobileTab:opacity-50 mobileMax:opacity-50 w-full"
        />
      </motion.div>
      <div className="mini-container z-[2] relative faq-section w-full">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0,
          }}
          className="remove-animation-fluctuation"
        >
          {data?.field_title && (
            <div className="flex justify-center items-center">
              <h3
                className={`${isLightBg
                  ? "bg-[linear-gradient(8.97deg,_#11375C_-26.31%,_#4992D3_154.54%)] bg-clip-text text-transparent"
                  : "!text-[#48DBB2] text-[42px]"
                  } mobileMax:text-center text-[32px] mt-[82px] text-center font-poppins mb-[40px] mobileMax:mb-8 font-semibold leading-normal text-transparent bg-clip-text mobileMax:text-[28px] mobileToDesk:mb-5 max-w-[800px]`}
              >
                {data?.field_title}
              </h3>
            </div>
          )}

          {data?.field_description && (
            <p
              className={`remove-animation-fluctuation text-xmedium ${isLightBg
                ? "text-[#003350] [&_a]:text-[#003350]"
                : "text-white [&_a]:text-[#48DBB2]"
                } leading-8 Faq-text mobileMax:text-xsmall mobileMax:leading-normal text-center [&_a]:underline [&_a]:underline-offset-4 [&_a]:font-semibold`}
              dangerouslySetInnerHTML={{
                __html: data?.field_description?.processed,
              }}
            />
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0,
          }}
          className="remove-animation-fluctuation w-full mb-[60px] mobileMax:mb-[25px]"
        >
          <div>
            {data?.field_add_question?.map((faq: any, index: number) => (
              <Link
                key={index}
                className="Faq-text cursor-pointer banner-greadient flex justify-between items-center gap-5 px-[47px] py-[23px] rounded-[20px] mb-[25px] mobileMax:!px-[20px] mobileMax:py-[15px] mobileMax:mb-5 text-[21px] font-semibold text-[#003350] font-poppins leading-normal hover:no-underline text-left mobileMax:text-xsmall"
                href={`${resolveLink(faq?.uri)}#${faq?.title
                  ?.toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {faq?.title}
                <FaChevronRight className="ml-2 text-medium text-[#003350]" />
              </Link>
            ))}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="remove-animation-fluctuation flex flex-col w-full items-center justify-center gap-[22px] p-0 mobileMax:flex-col mobileMax:w-full mobileMax:gap-[15px]"
        >
          {data?.field_add_section?.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="remove-animation-fluctuation flex flex-col w-full gap-[22px] p-0 mobileMax:flex-col mobileMax:w-full mobileMax:gap-[15px]"
            >
              {section?.field_title && (
                <h3
                  className={`${isLightBg ? "bg-[#11375C]" : "!text-[#48DBB2]"
                    } text-[32px] text-transparent bg-clip-text font-semibold text-clip text-left leading-normal mb-0 laptopMax:mb-2 mobileMax:text-small mobileMax:leading-7`}
                >
                  {section?.field_title}
                </h3>
              )}

              {data?.field_question_type === "with_link_only" ? (
                section?.field_add_question?.map((btn, index) => (
                  <Link
                    key={index}
                    href={resolveLink(btn?.field_question_link)}
                    target="_self"
                    className="flex items-center justify-between py-[23px] mobileMax:py-3 font-poppins font-[600] !text-[#003350] text-[20px] min-h-[63px] rounded-[20px] mobileMax:rounded-[10px] hover:!underline mobileMax:min-w-full mobileMax:w-full mobileMax:text-small bg-gradient-to-r from-[#48DBB2] to-[#A0DDFF] px-[47px] mobileMax:px-5"
                  >
                    {btn?.field_question}
                    {btn?.field_question.toLowerCase().includes("download") ? (
                      <BsDownload className="ml-2 text-odd" />
                    ) : (
                      <FaChevronRight className="ml-2 text-xmedium" />
                    )}
                  </Link>
                ))
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0,
                  }}
                  className="remove-animation-fluctuation w-full"
                >
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full Faq-text"
                  >
                    {section?.field_add_question?.map(
                      (faq: any, index: number) => {
                        const processHtml = (html: string) => {
                          return html.replace(
                            /src="\/(.*?)"/g,
                            `src="${config.apiBase}/$1"`,
                          );
                        };

                        return (
                          <AccordionItem
                            key={index}
                            value={`item-${index}`}
                            className="banner-greadient px-[47px] py-[23px] rounded-[20px] mb-[25px] mobileMax:px-[20px] mobileMax:py-[15px] mobileMax:mb-5"
                          >
                            <AccordionTrigger
                              bgColor="bg-[#003350]"
                              plusArrow
                              noArrow
                              className="text-[21px] !py-0 font-semibold text-[#fff] font-poppins leading-normal hover:no-underline text-left mobileMax:text-xsmall"
                            >
                              {faq?.question || faq?.field_question}
                            </AccordionTrigger>
                            <AccordionContent>
                              <p
                                dangerouslySetInnerHTML={{
                                  __html: processHtml(
                                    faq?.answer ||
                                    faq?.field_answer?.processed ||
                                    "",
                                  ),
                                }}
                                className="text-[#fff] font-poppins leading-normal pt-3 text-left mobileMax:text-xsmall"
                              />
                            </AccordionContent>
                          </AccordionItem>
                        );
                      },
                    )}
                  </Accordion>
                </motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {data?.field_links?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3 }}
          className="remove-animation-fluctuation flex items-center justify-center gap-[22px] p-0 mobileMax:flex-col mobileMax:w-full mobileMax:gap-[15px] mt-[60px] mobileMax:mt-[25px] mobileMax:px-3 mb-[80px] mobileMax:mb-[25px]"
        >
          {data?.field_links?.map((link: any, idx: number) => (
            <Link
              key={idx}
              href={resolveLink(link?.uri)}
              target="_blank"
              className="flex items-center justify-center min-w-[310px] font-poppins font-[600] !text-[#11055F] text-xmedium min-h-[63px] rounded-lg hover:!underline mobileMax:min-w-full mobileMax:w-full mobileMax:text-small bg-gradient-to-r from-[#48DBB2] to-[#A0DDFF]"
            >
              {link?.title}
              {link?.title?.toLowerCase().includes("download") ? (
                <BsDownload className="ml-2 text-odd" />
              ) : (
                <MdChevronRight className="ml-2 text-[30px]" />
              )}
            </Link>
          ))}
        </motion.div>
      )}
    </div>
  );
}
